import { useState, useEffect } from "react";
import { supabase, getStorageUrl } from "@/lib/supabase";
import { Image as ImageIcon, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/auth";

export default function AboutImageTab() {
  const [currentPoster, setCurrentPoster] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data, error } = await supabase.storage.from("images").list("about_posters", {
        limit: 1,
        sortBy: { column: "created_at", order: "desc" },
      });
      if (error) throw error;
      if (data && data.length > 0 && data[0].name !== ".emptyFolderPlaceholder") {
        setCurrentPoster({
          name: data[0].name,
          url: getStorageUrl(`about_posters/${data[0].name}`)
        });
      } else {
        setCurrentPoster(null);
      }
    } catch (e) {
      console.error("Failed to load about image:", e);
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const ext = file.name.split(".").pop();
      const fileName = `about_posters/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("images").upload(fileName, file);
      if (error) throw error;
      toast.success("About image uploaded successfully!");
      load();
    } catch (e2) {
      toast.error(formatApiError(e2) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!currentPoster) return;
    if (!window.confirm("Remove this image and revert to default?")) return;
    setBusy(true);
    try {
      const { error } = await supabase.storage.from("images").remove([`about_posters/${currentPoster.name}`]);
      if (error) throw error;
      toast.success("About image removed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-about-image-tab">
      <div className="mb-6">
        <h2 className="font-display font-black uppercase text-3xl md:text-4xl">About Page Image</h2>
        <p className="text-black/70">Update the image shown in the 'A candy-bright closet for every guy' section.</p>
      </div>

      <div className="nb-card p-6 md:p-8 max-w-2xl">
        <div className="flex flex-col items-center">
          <div className="w-full max-w-sm aspect-[4/5] rounded-[36px] nb-border overflow-hidden bg-[var(--muted)] mb-6 flex items-center justify-center">
            {currentPoster ? (
              <img src={currentPoster.url} alt="Current About Image" className="w-full h-full object-cover" />
            ) : (
              <div className="text-center text-black/40">
                <ImageIcon size={48} strokeWidth={2} className="mx-auto mb-2 opacity-50" />
                <p className="font-bold">Using Default Image</p>
              </div>
            )}
          </div>

          <div className="flex gap-4">
            <label className="nb-btn bg-[var(--primary)] text-white cursor-pointer inline-flex items-center gap-2">
              <Upload size={18} strokeWidth={3} />
              {busy ? "Uploading..." : "Upload New Image"}
              <input type="file" accept="image/*" onChange={upload} className="hidden" disabled={busy} />
            </label>
            {currentPoster && (
              <button onClick={remove} disabled={busy} className="nb-btn bg-black text-white inline-flex items-center gap-2">
                <Trash2 size={18} strokeWidth={3} /> Remove
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
